(function () {
  'use strict';

  var initializedAttribute = 'data-product-card-slider-initialized';
  var mobileAttribute = 'data-product-card-slider-mobile';
  var preloadObserver = null;
  var refreshFrame = 0;

  function isMobileCardMedia() {
    return Boolean(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
  }

  function canHover() {
    return Boolean(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  }

  function getImages(slider) {
    if (!slider) return [];
    return Array.prototype.filter.call(slider.children, function (child) {
      return child && child.tagName === 'IMG' && !child.hidden;
    });
  }

  function getOverlays(slider) {
    if (!slider) return [];
    return Array.prototype.filter.call(slider.children, function (child) {
      return child && child.classList && child.classList.contains('royal-pop-card-slide-overlay');
    });
  }

  function getButtons(slider) {
    if (!slider) return { previous: null, next: null };
    var media = slider.parentElement;
    var controls = null;
    if (media) {
      Array.prototype.some.call(media.children, function (child) {
        if (child && child.classList && child.classList.contains('card-product__media-controls')) {
          controls = child;
          return true;
        }
        return false;
      });
    }
    var root = controls || slider;
    return {
      previous: root.querySelector('.slider_left'),
      next: root.querySelector('.slider_right')
    };
  }

  function primeImage(image) {
    if (!image || image.dataset.productCardPrimed === 'true') return;
    image.dataset.productCardPrimed = 'true';
    image.loading = 'eager';
    if (image.decode) image.decode().catch(function () {});
  }

  function observeForPreload(slider) {
    if (!slider || isMobileCardMedia() || slider.dataset.productCardPreloadObserved === 'true') return;
    slider.dataset.productCardPreloadObserved = 'true';
    if (!('IntersectionObserver' in window)) {
      var images = getImages(slider);
      if (images[1]) primeImage(images[1]);
      return;
    }
    if (!preloadObserver) {
      preloadObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var targetImages = getImages(entry.target);
          if (targetImages[1]) primeImage(targetImages[1]);
          preloadObserver.unobserve(entry.target);
        });
      }, { rootMargin: '700px 0px' });
    }
    preloadObserver.observe(slider);
  }

  function updateButtons(slider, index) {
    var total = getImages(slider).length;
    var buttons = getButtons(slider);
    if (buttons.previous) {
      buttons.previous.hidden = total < 2;
      buttons.previous.disabled = total < 2 || index <= 0;
      buttons.previous.setAttribute('aria-disabled', buttons.previous.disabled ? 'true' : 'false');
    }
    if (buttons.next) {
      buttons.next.hidden = total < 2;
      buttons.next.disabled = total < 2 || index >= total - 1;
      buttons.next.setAttribute('aria-disabled', buttons.next.disabled ? 'true' : 'false');
    }
  }

  function clearInlineImageState(slider) {
    getImages(slider).forEach(function (image) {
      image.style.opacity = '';
      image.classList.remove('active');
    });
    getOverlays(slider).forEach(function (overlay) {
      overlay.classList.add('is-active');
    });
    delete slider.dataset.currentSlide;
  }

  function commitSlide(slider, index) {
    var images = getImages(slider);
    if (!images.length) return;
    var safeIndex = Math.max(0, Math.min(index, images.length - 1));

    images.forEach(function (image, imageIndex) {
      var active = imageIndex === safeIndex;
      image.style.opacity = active ? '1' : '0';
      image.classList.toggle('active', active);
    });

    getOverlays(slider).forEach(function (overlay) {
      var overlayIndex = Number.parseInt(overlay.dataset.slideIndex || '0', 10);
      overlay.classList.toggle('is-active', overlayIndex === safeIndex);
    });

    slider.dataset.currentSlide = String(safeIndex + 1);
    slider.dataset.slides = String(images.length);
    updateButtons(slider, safeIndex);

    slider.dispatchEvent(new CustomEvent('product-card:slide-change', {
      bubbles: true,
      detail: { index: safeIndex }
    }));
  }

  function setSlide(slider, index) {
    var images = getImages(slider);
    if (!images.length) return;
    var safeIndex = Math.max(0, Math.min(index, images.length - 1));
    var target = images[safeIndex];
    slider._requestedProductCardSlide = safeIndex;

    if (!target || (target.complete && target.naturalWidth > 0)) {
      commitSlide(slider, safeIndex);
      return;
    }

    primeImage(target);
    var ready = target.decode ? target.decode().catch(function () {}) : new Promise(function (resolve) {
      target.addEventListener('load', resolve, { once: true });
      target.addEventListener('error', resolve, { once: true });
    });
    Promise.resolve(ready).then(function () {
      if (slider._requestedProductCardSlide !== safeIndex) return;
      commitSlide(slider, safeIndex);
    });
  }

  function currentIndex(slider) {
    return Math.max(0, Number.parseInt(slider.dataset.currentSlide || '1', 10) - 1);
  }

  function stepSlide(slider, direction) {
    setSlide(slider, currentIndex(slider) + direction);
  }

  function stopCardNavigation(event) {
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
  }

  function bindArrow(button, slider, direction) {
    if (!button || button.dataset.productCardSliderBound === 'true') return;
    button.dataset.productCardSliderBound = 'true';

    ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'touchstart', 'touchend'].forEach(function (type) {
      button.addEventListener(type, function (event) {
        event.stopPropagation();
      }, true);
    });

    button.addEventListener('click', function (event) {
      stopCardNavigation(event);
      if (button.disabled) return;
      stepSlide(slider, direction);
    }, true);

    button.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      stopCardNavigation(event);
      if (button.disabled) return;
      stepSlide(slider, direction);
    }, true);
  }

  function initMobileSlider(slider) {
    clearInlineImageState(slider);
    slider.setAttribute(mobileAttribute, 'true');

    /* R44-204 — Related Products is already a horizontal Flickity rail. Its cards
       must never become a second horizontal touch surface. Keep the card on the
       first contextual image and let the outer rail own the gesture exclusively. */
    if (slider.closest('.related-products-slider')) {
      slider.dataset.productCardMode = 'outer-rail-owned';
      if (slider.scrollLeft) slider.scrollLeft = 0;
      slider.removeAttribute(initializedAttribute);
      return;
    }

    /* R44-192 — everywhere else on mobile, match the proven R44-175 contract:
       remove desktop state and let the browser own the horizontal media rail. */
    slider.dataset.productCardMode = 'native';
    slider.removeAttribute(initializedAttribute);
  }

  function initDesktopSlider(slider) {
    var images = getImages(slider);
    if (!images.length) return;

    slider.removeAttribute(mobileAttribute);
    slider.dataset.productCardMode = 'fade';
    commitSlide(slider, Math.min(currentIndex(slider), images.length - 1));
    observeForPreload(slider);

    var buttons = getButtons(slider);
    bindArrow(buttons.previous, slider, -1);
    bindArrow(buttons.next, slider, 1);

    var hoverTarget = slider.closest('.product-card-wrapper') || slider;
    if (hoverTarget.dataset.productCardSliderHoverBound !== 'true') {
      hoverTarget.dataset.productCardSliderHoverBound = 'true';
      hoverTarget.addEventListener('pointerenter', function (event) {
        if (!canHover() || isMobileCardMedia()) return;
        if (event.pointerType && event.pointerType !== 'mouse') return;
        if (getImages(slider).length > 1) setSlide(slider, 1);
      });
      hoverTarget.addEventListener('pointerleave', function (event) {
        if (!canHover() || isMobileCardMedia()) return;
        if (event.pointerType && event.pointerType !== 'mouse') return;
        setSlide(slider, 0);
      });
    }
  }

  function initSlider(slider) {
    if (!slider) return;
    if (!slider.hasAttribute(initializedAttribute)) slider.setAttribute(initializedAttribute, 'true');
    if (isMobileCardMedia()) initMobileSlider(slider);
    else initDesktopSlider(slider);
  }

  function initAll(root) {
    var scope = root || document;
    scope.querySelectorAll('.card-product__slider').forEach(initSlider);
  }

  function refreshAll() {
    if (refreshFrame) return;
    refreshFrame = window.requestAnimationFrame(function () {
      refreshFrame = 0;
      document.querySelectorAll('.card-product__slider').forEach(initSlider);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  window.addEventListener('resize', refreshAll, { passive: true });
  window.addEventListener('brilinton:layout-refresh', refreshAll);
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('brilinton:product-card-context-refresh', function (event) {
    var root = event.detail && event.detail.root ? event.detail.root : event.target;
    initAll(root && root.querySelectorAll ? root : document);
  });

  window.BrilintonProductCardMedia = {
    refresh: function (scope) { initAll(scope || document); },
    setSlide: setSlide
  };

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('.card-product__slider')) {
          initSlider(node);
          return;
        }
        if (node.querySelectorAll) initAll(node);
      });
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
